export type DurableLiveUpdateStatus =
  | "connecting"
  | "ready"
  | "reconnecting"
  | "resyncing"
  | "degraded"
  | "stopped";

export type DurableLiveUpdateChange = {
  cursor: string;
  type: string;
};

type ControlFrame =
  | { type: "stream.ready"; headCursor: string }
  | { type: "stream.heartbeat"; headCursor: string }
  | { type: "stream.resync_required"; reason: string }
  | { type: "stream.shutdown" };

export type DurableLiveUpdateFrame<TChange extends DurableLiveUpdateChange> =
  | ControlFrame
  | TChange;

type Socket = Pick<
  WebSocket,
  "close" | "onclose" | "onerror" | "onmessage" | "onopen" | "protocol"
>;

type Dependencies<TChange extends DurableLiveUpdateChange> = {
  buildUrl: (after: string) => string;
  createSocket?: (url: string, protocols: string[]) => Socket;
  getAccessToken: (forceRefresh?: boolean) => Promise<string>;
  initialCursor: string;
  onChanges: (changes: TChange[], signal: AbortSignal) => Promise<void>;
  onStatusChange?: (status: DurableLiveUpdateStatus) => void;
  parseFrame: (value: unknown) => DurableLiveUpdateFrame<TChange> | undefined;
  protocol: string;
  refreshSnapshot: (signal: AbortSignal) => Promise<{ cursor: string }>;
};

const CHANGE_COALESCE_MILLISECONDS = 200;
const MAX_RESYNCS_WITHOUT_STABILITY = 3;

export const createDurableLiveUpdatesController = <
  TChange extends DurableLiveUpdateChange,
>(
  dependencies: Dependencies<TChange>,
) => {
  const createSocket =
    dependencies.createSocket ??
    ((url: string, protocols: string[]) => new WebSocket(url, protocols));
  let appliedCursor = dependencies.initialCursor;
  let authorizationRecoveryUsed = false;
  let connectionGeneration = 0;
  let disposed = false;
  let flushInProgress = false;
  let flushTimer: number | undefined;
  let reconnectAttempt = 0;
  let reconnectTimer: number | undefined;
  let refreshController: AbortController | undefined;
  let resyncCount = 0;
  let resyncInProgress = false;
  let pendingHeadCursor: string | undefined;
  let socket: Socket | undefined;
  let stableTimer: number | undefined;
  let status: DurableLiveUpdateStatus = "stopped";
  const pendingChanges: TChange[] = [];
  const pendingCursors = new Set<string>();

  const isDisposed = () => disposed;
  const isAborted = (controller: AbortController) => controller.signal.aborted;
  const isCurrentGeneration = (generation: number) =>
    generation === connectionGeneration;

  const setStatus = (next: DurableLiveUpdateStatus) => {
    status = next;
    dependencies.onStatusChange?.(next);
  };

  const scheduleReconnect = (forceTokenRefresh = false) => {
    if (disposed) return;
    setStatus("reconnecting");
    const delay =
      Math.random() * Math.min(1_000 * 2 ** reconnectAttempt, 30_000);
    reconnectAttempt += 1;
    window.clearTimeout(reconnectTimer);
    reconnectTimer = window.setTimeout(() => {
      void connect(forceTokenRefresh);
    }, delay);
  };

  const scheduleFlush = () => {
    if (
      disposed ||
      flushInProgress ||
      flushTimer !== undefined ||
      !pendingChanges.length
    ) {
      return;
    }
    flushTimer = window.setTimeout(
      () => void flush(),
      CHANGE_COALESCE_MILLISECONDS,
    );
  };

  const persistDeliveredHeadCursor = () => {
    if (
      flushInProgress ||
      pendingChanges.length ||
      resyncInProgress ||
      !pendingHeadCursor
    ) {
      return;
    }
    appliedCursor = pendingHeadCursor;
    pendingHeadCursor = undefined;
  };

  const flush = async () => {
    flushTimer = undefined;
    const changes = pendingChanges.splice(0);
    pendingCursors.clear();
    if (!changes.length || disposed) return;
    flushInProgress = true;
    const controller = new AbortController();
    refreshController = controller;
    try {
      await dependencies.onChanges(changes, controller.signal);
      if (isDisposed() || isAborted(controller)) return;
      appliedCursor = changes.at(-1)?.cursor ?? appliedCursor;
      persistDeliveredHeadCursor();
    } catch {
      if (!isDisposed() && !isAborted(controller)) void resync();
    } finally {
      if (refreshController === controller) refreshController = undefined;
      flushInProgress = false;
      scheduleFlush();
    }
  };

  const queueChange = (change: TChange) => {
    if (pendingCursors.has(change.cursor)) return;
    pendingCursors.add(change.cursor);
    pendingChanges.push(change);
    scheduleFlush();
  };

  const resync = async (forceTokenRefresh = false) => {
    if (disposed || resyncInProgress) return;
    if (resyncCount >= MAX_RESYNCS_WITHOUT_STABILITY) {
      connectionGeneration += 1;
      refreshController?.abort();
      window.clearTimeout(flushTimer);
      flushTimer = undefined;
      window.clearTimeout(stableTimer);
      window.clearTimeout(reconnectTimer);
      pendingChanges.length = 0;
      pendingCursors.clear();
      pendingHeadCursor = undefined;
      setStatus("degraded");
      socket?.close(1000, "Live update resync circuit open");
      socket = undefined;
      return;
    }
    resyncCount += 1;
    resyncInProgress = true;
    setStatus("resyncing");
    connectionGeneration += 1;
    window.clearTimeout(flushTimer);
    flushTimer = undefined;
    window.clearTimeout(reconnectTimer);
    window.clearTimeout(stableTimer);
    pendingChanges.length = 0;
    pendingCursors.clear();
    pendingHeadCursor = undefined;
    refreshController?.abort();
    const controller = new AbortController();
    refreshController = controller;
    try {
      socket?.close(1000, "Canonical resynchronization required");
      socket = undefined;
      const refreshed = await dependencies.refreshSnapshot(controller.signal);
      if (isDisposed() || isAborted(controller)) return;
      appliedCursor = refreshed.cursor;
      await connect(forceTokenRefresh);
    } catch {
      if (!isDisposed() && !isAborted(controller)) setStatus("degraded");
    } finally {
      resyncInProgress = false;
    }
  };

  const connect = async (forceTokenRefresh = false) => {
    if (disposed) return;
    const generation = ++connectionGeneration;
    setStatus(status === "stopped" ? "connecting" : "reconnecting");
    try {
      const accessToken = await dependencies.getAccessToken(forceTokenRefresh);
      if (isDisposed() || !isCurrentGeneration(generation)) return;
      const currentSocket = createSocket(dependencies.buildUrl(appliedCursor), [
        dependencies.protocol,
        accessToken,
      ]);
      socket = currentSocket;
      currentSocket.onopen = () => {
        if (currentSocket.protocol !== dependencies.protocol) {
          currentSocket.close(1000, "Unexpected live update protocol");
        }
      };
      currentSocket.onmessage = (event) => {
        if (disposed || generation !== connectionGeneration) return;
        let value: unknown;
        try {
          value = JSON.parse(String(event.data));
        } catch {
          void resync();
          return;
        }
        const frame = dependencies.parseFrame(value);
        if (!frame) {
          void resync();
          return;
        }
        if (frame.type === "stream.ready") {
          if (!("headCursor" in frame)) {
            void resync();
            return;
          }
          pendingHeadCursor = frame.headCursor;
          persistDeliveredHeadCursor();
          setStatus("ready");
          window.clearTimeout(stableTimer);
          stableTimer = window.setTimeout(() => {
            reconnectAttempt = 0;
            resyncCount = 0;
            authorizationRecoveryUsed = false;
          }, 60_000);
          return;
        }
        if (frame.type === "stream.heartbeat") {
          if (!("headCursor" in frame)) {
            void resync();
            return;
          }
          pendingHeadCursor = frame.headCursor;
          persistDeliveredHeadCursor();
          return;
        }
        if (frame.type === "stream.resync_required") {
          void resync();
          return;
        }
        if (frame.type === "stream.shutdown") {
          currentSocket.close(1000, "Gateway restarting");
          return;
        }
        queueChange(frame as TChange);
      };
      currentSocket.onerror = () => {
        currentSocket.close();
      };
      currentSocket.onclose = (event) => {
        if (disposed || generation !== connectionGeneration) return;
        socket = undefined;
        window.clearTimeout(stableTimer);
        if (event.code === 1008) {
          if (authorizationRecoveryUsed) {
            setStatus("degraded");
            return;
          }
          authorizationRecoveryUsed = true;
          void resync(true);
          return;
        }
        scheduleReconnect();
      };
    } catch {
      if (!isDisposed() && isCurrentGeneration(generation)) scheduleReconnect();
    }
  };

  return {
    getAppliedCursor: () => appliedCursor,
    getStatus: () => status,
    resync: () => {
      void resync();
    },
    reconnectNow: () => {
      if (disposed) return;
      if (status === "degraded") {
        // A deliberate retry/online event starts a new bounded recovery cycle.
        // Always refresh first: the last snapshot may have failed or access changed.
        resyncCount = 0;
        authorizationRecoveryUsed = false;
        void resync(true);
        return;
      }
      if (status !== "reconnecting") return;
      window.clearTimeout(reconnectTimer);
      void connect();
    },
    start: () => {
      if (!disposed && status !== "stopped") return;
      disposed = false;
      void connect();
    },
    stop: () => {
      disposed = true;
      connectionGeneration += 1;
      refreshController?.abort();
      window.clearTimeout(flushTimer);
      window.clearTimeout(reconnectTimer);
      window.clearTimeout(stableTimer);
      socket?.close(1000, "Live update consumer disposed");
      socket = undefined;
      pendingChanges.length = 0;
      pendingCursors.clear();
      pendingHeadCursor = undefined;
      setStatus("stopped");
    },
  };
};
